/**
 * Build the chat prompt for "Edit tile with AI" requests.
 *
 * The tile-edit-popover collects a natural-language change request from
 * the user ("make it red", "change to LineChart", "show last 7 days
 * only"). The agent receives the prompt with:
 *   - The element path of the tile being edited (e.g. `children[1].children[0]`)
 *   - A JSON snapshot of the tile's current spec so it knows what's there
 *   - The user's change request in prose
 *
 * The agent emits a `yaml-patch` with one or more RFC 6902 `replace` / `add`
 * operations targeting fields on that one tile. The renderer applies the
 * patch surgically — every other tile is untouched.
 *
 * Pure: no React, no fetch. Tested separately so the prompt-building
 * stays predictable as the dialog UI evolves.
 */

import type { DashboardElement, QueryBinding } from "../dashboard/catalog"

export interface EditTilePromptInput {
  /** User's natural-language change request. Must be non-empty. */
  description: string
  /** Dotted path to the tile within spec.children. */
  elementPath: string
  /** Snapshot of the tile's current spec. Used by the agent to know
   *  which fields exist before deciding which to mutate. Trimmed to
   *  just structural fields (no resolved data arrays) so the prompt
   *  stays bounded — the agent doesn't need the full bound `data`
   *  to know how to change `color` or `title`. */
  tile: DashboardElement
  /** All query bindings on the dashboard. We match by element_path to
   *  surface the tile's SQL, latest error, and a small data sample so
   *  the agent can answer questions like "why isn't this SQL working".
   *  Without these, the agent only sees the tile shell and has nothing
   *  to investigate when the user asks about data-shape issues. */
  queryBindings?: QueryBinding[]
}

/**
 * Compose the chat prompt the user submits when they click "Skicka" on
 * the tile-edit-with-AI dialog. The resulting string is what the parent
 * dispatches into the chat pipeline.
 *
 * Returns ``null`` for invalid input (empty description, missing path).
 */
export function buildEditTilePrompt(input: EditTilePromptInput): string | null {
  const description = input.description.trim()
  if (!description) return null
  if (!input.elementPath) return null

  // Strip resolved data fields so the agent sees structure only.
  // `data`/`rows`/`value` get refilled by the refresh path; including
  // them here just bloats the prompt and risks the agent confusing
  // current values with structural intent. We surface a SMALL data
  // sample separately below so the agent can answer questions like
  // "why isn't this SQL working" without seeing the full series.
  const trimmedTile = stripResolvedFields(input.tile)
  const tileJson = JSON.stringify(trimmedTile, null, 2)

  // Locate the binding for this tile, if any, and pull SQL + status.
  // Server-side `_BINDING_HINT_FIELDS` strips inline `_sql` on save so
  // the tile spec by itself never carries the query — without this the
  // agent has no idea what query it's looking at.
  const binding = (input.queryBindings ?? []).find((b) => b.element_path === input.elementPath)
  const dataSample = pickDataSample(input.tile)
  const bindingBlock = buildBindingBlock(binding, dataSample)

  return [
    `Edit ONE tile in the dashboard at path \`${input.elementPath}\`. The user wants to change it; you emit a yaml-patch with one or more RFC 6902 \`replace\` / \`add\` / \`remove\` operations rooted at \`/${input.elementPath.replace(/\./g, "/").replace(/\[(\d+)\]/g, "/$1")}\`.`,
    "",
    "User change request:",
    `> ${description}`,
    "",
    "Tile's CURRENT structure (resolved data fields stripped so you see authoring intent only):",
    "",
    "```json",
    tileJson,
    "```",
    bindingBlock,
    "",
    "STEP 1 — Decide if the change is answerable from the existing tile + connected data:",
    "- Cosmetic changes (color, title, label, sort order, type-switch, threshold) → straightforward yaml-patch.",
    "- Data-shape changes (different aggregation, different grouping, different filter) → update the `_sql` field on the tile AND the structural fields that depend on it (xKey, bars[].dataKey, columns[]).",
    "- Requests that need data not in the connection → refuse in prose and suggest 1–3 answerable alternatives. Do NOT emit a patch.",
    "",
    "STEP 2 — Emit a `yaml-patch` fenced code block with the surgical operations. Do NOT regenerate the whole tile; do NOT touch other tiles. Example for 'change the color to red':",
    "",
    "```yaml-patch",
    `- op: replace`,
    `  path: /${input.elementPath.replace(/\./g, "/").replace(/\[(\d+)\]/g, "/$1")}/bars/0/color`,
    // eslint-disable-next-line no-useless-escape
    `  value: \"#ef4444\"`,
    "```",
    "",
    "For SQL-shape changes, replace the `_sql` (and any structural fields the new query implies):",
    "",
    "```yaml-patch",
    `- op: replace`,
    `  path: /${input.elementPath.replace(/\./g, "/").replace(/\[(\d+)\]/g, "/$1")}/_sql`,
    `  value: |`,
    `    SELECT region, AVG(amount) AS avg_amount FROM sales GROUP BY region ORDER BY avg_amount DESC`,
    `- op: replace`,
    `  path: /${input.elementPath.replace(/\./g, "/").replace(/\[(\d+)\]/g, "/$1")}/bars/0/dataKey`,
    `  value: avg_amount`,
    `- op: replace`,
    `  path: /${input.elementPath.replace(/\./g, "/").replace(/\[(\d+)\]/g, "/$1")}/bars/0/label`,
    `  value: Genomsnitt`,
    "```",
    "",
    "After the patch lands, the tile's binding refreshes from the connection in seconds — no agent loop needed for the data fetch. Briefly confirm in prose what you changed (one sentence is enough).",
  ].join("\n")
}

function stripResolvedFields(tile: DashboardElement): unknown {
  if (!tile || typeof tile !== "object") return tile
  const RESOLVED = new Set(["data", "rows", "value"])
  const out: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(tile as Record<string, unknown>)) {
    if (RESOLVED.has(key)) continue
    out[key] = val
  }
  return out
}

/**
 * Pull a tiny representative sample from the tile's resolved data —
 * 3 rows max for charts/tables, 1 row for KPICard. Used to give the
 * agent enough signal to answer "why is the data wrong" without
 * dumping the full series into the prompt.
 */
function pickDataSample(tile: DashboardElement): unknown {
  if (!tile || typeof tile !== "object") return null
  const t = tile as Record<string, unknown>
  const data = t.data
  if (Array.isArray(data)) return data.slice(0, 3)
  const rows = t.rows
  if (Array.isArray(rows)) return rows.slice(0, 3)
  if ("value" in t) return { value: t.value }
  return null
}

function buildBindingBlock(binding: QueryBinding | undefined, dataSample: unknown): string {
  if (!binding && dataSample == null) return ""
  const parts: string[] = [
    "",
    "Tile's BINDING + data context (use this when the user asks about data, SQL, errors, or empty values):",
    "",
  ]
  if (binding) {
    parts.push("```sql")
    parts.push(binding.sql || "(no SQL)")
    parts.push("```")
    parts.push("")
    if (binding.last_error) {
      parts.push(`Last SQL error: \`${binding.last_error}\``)
      parts.push("")
    } else if (binding.last_success) {
      parts.push(`SQL last ran successfully at ${binding.last_success}.`)
      parts.push("")
    }
  }
  if (dataSample != null) {
    parts.push("Data sample (first ~3 rows, exactly as the renderer received them):")
    parts.push("")
    parts.push("```json")
    parts.push(JSON.stringify(dataSample, null, 2))
    parts.push("```")
  }
  return parts.join("\n")
}
